import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client'
import { AccountRow } from './AccountRow'
import { Sheet } from './Sheet'

/*
  Starting over without starting again.

  Next to closing the account in Settings, and deliberately not folded into it. They look
  similar and are not: one ends the company and everyone's access to it, the other empties a
  company that carries on. Somebody who wants an empty table should not have to read a
  screen about deleting their account to find out whether that is what they are about to do,
  and somebody closing the account should not be offered a milder option at the last moment.

  What survives is the part worth saying out loud, because it is the reason this exists
  rather than the delete button: the login, the team, the company profile, the board the
  owner tuned, and the inbound email address. A permalink is claimed once and never
  reissued, so clearing the history must never burn the address already sitting in somebody's
  mail rule.
*/

export interface ResetImpact {
  company: string
  meetings: number
  items: number
  commitments: number
  consults: number
  documents: number
  figures: number
}

/**
 * Reads what clearing would destroy. Null while loading, and on any failure, which draws
 * no card: a settings screen that cannot say what it is about to delete has no business
 * offering the button.
 */
function useResetImpact(enabled: boolean): ResetImpact | null {
  const [impact, setImpact] = useState<ResetImpact | null>(null)

  useEffect(() => {
    if (!enabled) return
    api.get<ResetImpact>('/api/account/reset').then(setImpact).catch(() => setImpact(null))
  }, [enabled])

  return impact
}

/*
  Shown to the owner alone. The endpoint refuses anybody else with a 403, and this is the
  same rule said earlier: a colleague should not have to type the company name to find out
  that clearing its history was never theirs to do. Hiding it is not the security boundary,
  the 403 is.
*/
export function ClearHistoryCard({ role }: { role: string }) {
  const isOwner = role.toLowerCase() === 'owner'
  const impact = useResetImpact(isOwner)
  const [open, setOpen] = useState(false)

  if (!isOwner || !impact) return null

  const nothing = impact.meetings === 0 && impact.items === 0
    && impact.commitments === 0 && impact.consults === 0 && impact.documents === 0

  return (
    <div className="card card--rows">
      <div className="card__title">Starting over</div>
      <AccountRow
        label="Clear the history"
        note={nothing
          ? 'There is nothing on this board yet, so there is nothing to clear.'
          : 'Empties the table, the meetings, the actions and the file cabinet. '
            + 'Your board, your profile and your email address stay as they are.'}
        action={
          <button className="btn btn--small btn--danger" disabled={nothing}
                  onClick={() => setOpen(true)}>
            Clear
          </button>
        }
      />
      {open && <ConfirmClear impact={impact} onClose={() => setOpen(false)} />}
    </div>
  )
}

function ConfirmClear({ impact, onClose }: { impact: ResetImpact; onClose: () => void }) {
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const ready = typed.trim().toLowerCase() === impact.company.trim().toLowerCase()

  const go = useCallback(async () => {
    if (busy || !ready) return
    setBusy(true)
    setError(null)
    try {
      await api.post<void>('/api/account/reset', { confirm: typed })
      /*
        A hard navigation for the same reason closing does one. Every screen behind this is
        holding meetings, items and figures that no longer exist, and half of them would
        keep showing them until something happened to make them refetch.
      */
      window.location.href = '/'
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not clear the history')
      setBusy(false)
    }
  }, [busy, ready, typed])

  return (
    <Sheet title="Clear the history" onClose={onClose}>
      <p className="muted" style={{ marginBottom: 'var(--space-3)' }}>
        This empties <strong>{impact.company}</strong> and keeps the company. It is immediate
        and there is no copy kept.
      </p>

      <ul className="muted" style={{ margin: '0 0 var(--space-3)', paddingLeft: '1.2em' }}>
        <li>{count(impact.meetings, 'meeting')}, with every position and vote in them</li>
        <li>{count(impact.items, 'item')} on the table, and {count(impact.consults, 'consult').toLowerCase()}</li>
        <li>{count(impact.commitments, 'commitment')}</li>
        <li>
          {count(impact.documents, 'document')} and {count(impact.figures, 'figure').toLowerCase()}{' '}
          read out of them
        </li>
      </ul>

      {/* The half nobody reads a warning to find out, and the half that makes this
          different from deleting the account. */}
      <p className="muted" style={{ marginBottom: 'var(--space-4)' }}>
        Your sign-in, anyone else on the board, your company profile, the seats you have
        tuned and your <strong>@in.boardium.co</strong> address all stay exactly as they are.
      </p>

      <label className="field">
        <span className="field__label">
          Type <strong>{impact.company}</strong> to confirm
        </span>
        <input className="input" value={typed} autoFocus
               onChange={e => setTyped(e.target.value)} />
      </label>

      {error && <div className="signin__error">{error}</div>}

      <div className="sheet__actions">
        <button type="button" className="btn" onClick={onClose}>Cancel</button>
        <button type="button" className="btn btn--danger" disabled={busy || !ready}
                onClick={() => void go()}>
          {busy ? 'Clearing' : 'Clear the history'}
        </button>
      </div>
    </Sheet>
  )
}

/** "3 meetings", "One document", "No commitments". Zero reads better as a word. */
function count(n: number, one: string, many?: string): string {
  const plural = many ?? `${one}s`
  if (n === 0) return `No ${plural}`
  if (n === 1) return `One ${one}`
  return `${n} ${plural}`
}
